import { DateTime, DurationLike, Interval } from "luxon";

/**
 * Function that can be used to convert a date string (assumed to be in UTC) to a Date with the local time.
 * @param date Input date string (assumed to be in UTC)
 * @returns The JS Date converted to the local time
 */
export function convertUtcToLocalDateAndTime(date: string): Date {
  const utcDateAndTime = DateTime.fromISO(date, { zone: "utc" });
  return utcDateAndTime.setZone(DateTime.local().zoneName).toJSDate();
}

/**
 * Function that accept an ISO string, JS Date object, or undefined and returns a JS Date object or undefined. ISO strings will be converted to local time.
 * @param date Input date string (assumed to be in UTC), JS Date object, or undefined
 * @returns The JS Date converted to the local time
 */
export function getDateObject(date?: string | Date): Date | undefined {
  if (!date) return undefined;
  else if (date instanceof Date) {
    return date;
  } else return convertUtcToLocalDateAndTime(date);
}

/**
 * Function that can be used to get a date with a time of 00:00:00 (start of day). Milliseconds are set to 000.
 * @param date Input JS date
 * @returns The JS Date  with a time of 00:00
 */
export function getStartOfDay(date: Date): Date {
  return DateTime.fromJSDate(date).startOf("day").startOf("second").toJSDate();
}

/**
 * Function that can be used to get a date with a time of 23:59 (end of day). Milliseconds are set to 000.
 * @param date Input JS date
 * @returns The JS Date with a time of 23:59:59
 */
export function getEndOfDay(date: Date): Date {
  return DateTime.fromJSDate(date).endOf("day").startOf("second").toJSDate();
}

/**
 * Function that generates an array of recurring dates at a set interval between a start date and an end date.
 * @param startDate Start JS date
 * @param endDate End JS date
 * @param interval Luxon DurationLike object to set the interval between dates
 * @returns An array of JS dates
 */
export function getRecurringDatesByEndDate(startDate: Date, endDate: Date, interval: DurationLike) {
  const intervalStartDate = DateTime.fromJSDate(startDate);
  const intervalEndDate = DateTime.fromJSDate(endDate);

  return Interval.fromDateTimes(intervalStartDate.startOf("day"), intervalEndDate.endOf("day"))
    .splitBy(interval)
    .map((d) => d.start.toJSDate());
}

/**
 * Function that generates an array of recurring dates at a set interval for a set number of occurrences.
 * @param startDate Start JS date
 * @param occurrenceCount The number of occurrences
 * @param interval Luxon DurationLike object to set the interval between dates
 * @returns An array of JS dates
 */
export function getRecurringDatesByOccurrenceCount(startDate: Date, occurrenceCount: number, interval: DurationLike) {
  let recurringDate = DateTime.fromJSDate(startDate);

  const recurringDates = [startDate];

  for (let i = 1; i < occurrenceCount; i++) {
    recurringDate = recurringDate.plus(interval);
    recurringDates.push(recurringDate.toJSDate());
  }

  return recurringDates;
}
